import { createEntityAdapter, EntityAdapter } from '@ngrx/entity';
import { createReducer, on } from '@ngrx/store';
import { WoundStatusType } from '@mona/models';
import { EncountersActions } from '@mona/pdms/data-access-encounters';
import { WoundStatusState } from '../../entities';
import { WoundStatusAction } from '../actions';

export const woundStatusAdapter: EntityAdapter<WoundStatusType> = createEntityAdapter<WoundStatusType>();

export const initialState: WoundStatusState = woundStatusAdapter.getInitialState({
    error: null,
    isLoading: false,
});

export const woundStatusReducer = createReducer(
    initialState,
    on(WoundStatusAction.loadWoundStatus, state => ({ ...state, isLoading: true })),
    on(WoundStatusAction.loadWoundStatusSuccess, (state, { data }) =>
        // Use upsertMany instead of setAll so optimistic local entries (added/updated between
        // dispatching `loadWoundStatus` and the response arriving, or by a WS push that landed
        // before the GET completed) are not wiped out. Deletions propagate via the dedicated
        // `addWoundStatusChanges` action (WS push), so we don't need `setAll`'s removal semantics.
        woundStatusAdapter.upsertMany(data, {
            ...state,
            error: null,
            isLoading: false,
        }),
    ),
    on(WoundStatusAction.loadWoundStatusFailure, (state, { error }) => ({
        ...state,
        error,
        isLoading: false,
    })),
    on(WoundStatusAction.loadWoundStatusCancelled, state => ({
        ...state,
        isLoading: false,
        error: null,
    })),
    on(WoundStatusAction.updateWoundStatus, (state, action) => woundStatusAdapter.updateMany(action.update, state)),
    on(WoundStatusAction.upsertWoundStatus, (state, action) => woundStatusAdapter.upsertMany(action.data, state)),
    on(WoundStatusAction.removeWoundStatus, (state, { ids }) => woundStatusAdapter.removeMany(ids, state)),
    /**
     * Defensive reset for the MD-9750 stuck-spinner family.
     */
    on(WoundStatusAction.clearWoundStatus, EncountersActions.clearEncounter, state =>
        woundStatusAdapter.removeAll({
            ...state,
            isLoading: false,
            error: null,
        }),
    ),
    on(WoundStatusAction.addWoundStatusChanges, (state, { toRemoveIds, toUpdateEntities }) => {
        const newState = woundStatusAdapter.removeMany(toRemoveIds, state);

        return woundStatusAdapter.upsertMany(toUpdateEntities, newState);
    }),
);
